import { forOwn } from 'lodash'
import { useCallback } from 'react'

import { Status, useFetchRefactored } from 'civic-champs-shared/api/hooks'

export interface GetEventQueueStatusFlags {
  queued?: boolean
  running?: boolean
  completed?: boolean
}

export interface EventHandlerTask {
  id: string
  priority: number
  handlerName: string
  eventDetails: string
  receivedAt: string
  startedAt?: string
  completedAt?: string
  success?: boolean
  workerId?: string
}

export interface WorkerQueueStatus {
  workerId: string
  updatedAt: string
  maxConcurrency: number
  highPriorityReserve: number
  runningCount: number
  queueLength: number
  oldestTask?: string | null
}

export interface EventQueueStatus {
  maxConcurrency: number
  highPriorityReserve: number
  runningCount: number
  queueLength: number
  oldestTask?: string | Date | null
  queuedTasks?: EventHandlerTask[]
  runningTasks?: EventHandlerTask[]
  completedTasks?: EventHandlerTask[]
  completedTaskRetention?: string
  aggregated?: boolean
  workerCount?: number
  workers?: WorkerQueueStatus[]
}

export interface LoadSimulatorPayload {
  durationInMs: number
  batchSize: number
  priority: number
  failureRate?: number
  beginLoadAt?: Date
  minEventDelay?: number
  maxEventDelay?: number
}

const useGetEventQueueStatus = (): [
  (flags?: GetEventQueueStatusFlags | undefined) => Promise<EventQueueStatus>,
  Status<EventQueueStatus>,
] => {
  const [request, requestStatus] = useFetchRefactored<EventQueueStatus>({
    errorMessage: `Failed to fetch EventHandler Queue Status`,
  })

  const getQueueStatus = useCallback(
    (flags?: GetEventQueueStatusFlags) => {
      const include: string[] = []
      forOwn(flags, (value, key) => {
        if (value) include.push(key)
      })

      return request({
        method: 'get',
        url: '/monitoring/event-handler-queue',
        config: include.length
          ? {
              queryStringParameters: {
                include: include.join(','),
              },
            }
          : undefined,
      })
    },
    [request],
  )

  return [getQueueStatus, requestStatus]
}

export const useSubmitLoadSimulator = () => {
  const [request, requestStatus] = useFetchRefactored<void>({
    errorMessage: `Failed to submit load simulator`,
    successMessage: 'Load simulator events submitted successfully',
  })

  const submitLoadSimulator = useCallback(
    (payload: LoadSimulatorPayload) => {
      return request({
        method: 'post',
        url: '/monitoring/event-handler-queue',
        config: {
          body: payload,
        },
      })
    },
    [request],
  )

  return [submitLoadSimulator, requestStatus] as const
}

export default useGetEventQueueStatus
